import { DateRangeIcon, FlashOnIcon, Grid, makeStyles, SvgIconProps, Theme } from '@freighthub/silhouette-core'
import { ShipmentMode } from 'features/ShipmentDetails'
import { TransportModeIcon, TransportTypeIcon } from 'features/TransportPlan'
import { ComponentType, FC, ReactNode } from 'react'
import { formatDate } from 'utils/date'
import { useI18n } from 'utils/localisation'

import { cargoString } from '../../Cargo'
import { modeTerm } from '../Mode'
import { IQuoteSheet } from '../QuoteSheetModel'

const useStyles = makeStyles<Theme>((theme) => ({
  modeCargoSection: {
    backgroundColor: theme.palette.grey[300],
    padding: theme.spacing(2),
    flex: '1 0 0',
    maxWidth: '250px',
  },
  mode: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  modeIcon: {
    display: 'inline-block',
    borderRadius: '20px',
    padding: '6px 6px 0',
    minWidth: '38px',
    textAlign: 'center',
    margin: '0 2px',
    backgroundColor: theme.palette.grey[400],
    color: theme.palette.grey[900],
  },
  iconText: {
    fontSize: theme.typography.fontSize,
    display: 'flex',
    color: theme.palette.grey[600],
    marginBottom: theme.spacing(1),
    '&:last-of-type': {
      marginBottom: 0,
    },
  },
  description: {
    alignSelf: 'center',
  },
  iconWrapper: {
    display: 'inline-block',
    marginRight: theme.spacing(2),
  },
  icon: {
    width: '18px',
    height: '18px',
  },
}))

interface TransportModeDetailsProps {
  modes: ShipmentMode[]
}

const TransportModeDetails: FC<TransportModeDetailsProps> = ({ modes }) => {
  const classes = useStyles()
  const { translate } = useI18n()
  return (
    <div className={classes.mode}>
      <div>
        {modes.map((mode, i) => (
          <div className={classes.modeIcon} key={i}>
            <TransportModeIcon transportMode={mode} />
          </div>
        ))}
      </div>
      <div>
        {modes.map((mode, i) => (
          <span key={i}>
            {translate(modeTerm[mode])}
            {separator(' - ', modes, i)}
          </span>
        ))}
      </div>
    </div>
  )
}

interface ModeCargoTileProps {
  quoteSheet: IQuoteSheet
}

const separator = (separator: string, modes, i) =>
  modes.length > 0 && modes.length > i + 1 ? <span>{separator}</span> : null

export const ModeCargoTile: FC<ModeCargoTileProps> = ({ quoteSheet }) => {
  const { modes, cargo, cargoReadyAt, services, type } = quoteSheet.request
  const localisationStore = useI18n()
  const { translate } = localisationStore
  const classes = useStyles()
  const icon = <TransportTypeIcon transportType={type} className={classes.icon} />

  return (
    <Grid className={classes.modeCargoSection}>
      <TransportModeDetails modes={modes} />
      <IconAndDesc
        desc={
          <div>
            {cargo.map((c, i) => (
              <div key={i}>{cargoString(c, localisationStore)}</div>
            ))}
            {/* @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'. */}
            {services.isStackable && type === 'lcl' && translate('quotesheet_header-stackable')}
          </div>
        }
        // eslint-disable-next-line react/no-unstable-nested-components
        Icon={() => icon}
      />
      {cargoReadyAt && (
        <IconAndDesc
          desc={translate('manual_quote-cargo_ready_at', {
            date: formatDate(cargoReadyAt),
          })}
          Icon={DateRangeIcon}
        />
      )}
      {/* @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'. */}
      {services.dangerousGoods && (
        <IconAndDesc desc={translate('common-dangerous_or_special_goods')} Icon={FlashOnIcon} />
      )}
    </Grid>
  )
}

interface IconDescProps {
  Icon: ComponentType<SvgIconProps>
  desc: ReactNode
}

const IconAndDesc: FC<IconDescProps> = ({ Icon, desc }) => {
  const classes = useStyles()
  return (
    <div className={classes.iconText}>
      <div className={classes.iconWrapper}>
        <Icon color="action" className={classes.icon} />
      </div>
      <div className={classes.description}>{desc}</div>
    </div>
  )
}
